import type {
  PortalsApprovalRequest,
  PortalsApprovalResponse,
  PortalsEstimateRequest,
  PortalsEstimateResponse,
  PortalsExecuteRequest,
  PortalsExecuteResponse,
} from "./types"

export class PortalsApiTransport {
  private readonly baseUrl = "https://api.portals.fi/v2"

  /**
   * Estimate the output of a portal transaction
   *
   * @param request - The estimate request parameters
   * @returns Promise resolving to the estimate response
   */
  async estimate(request: PortalsEstimateRequest): Promise<PortalsEstimateResponse> {
    const params = new URLSearchParams({
      inputToken: request.inputToken,
      inputAmount: request.inputAmount,
      outputToken: request.outputToken,
    })

    const url = `${this.baseUrl}/portal/estimate?${params}`
    // console.log("Portals API URL:", url) // Debug log to verify URL format

    const response = await fetch(url)

    if (!response.ok) {
      // console.log("", response.status)

      if (response.status === 503) {
        // {message: "Service Unavailable"}
        throw new Error("Portals Zap API is currently unavailable")
      }

      const json = await response.json()

      // console.log("estimate response", response)
      // console.log("json", json)
      if (json.message) {
        throw new Error(json.message)
      }

      throw new Error(`Portals API error: ${response.status} ${response.statusText}`)
    }

    const json = await response.json()
    // console.log("Portals API Estimate Response:", json)

    return json
  }

  /**
   * Execute a portal transaction
   *
   * @param request - The execute request parameters
   * @returns Promise resolving to the transaction response
   */
  async execute(request: PortalsExecuteRequest): Promise<PortalsExecuteResponse> {
    const params = new URLSearchParams({
      inputToken: request.inputToken,
      inputAmount: request.inputAmount,
      outputToken: request.outputToken,
      sender: request.sender,
      validate: "true",
      partner: "0x9A134D3BdEB95C8C8773bD7627f1FDD792531C97",
      feePercentage: "0.13",
      //   slippageTolerancePercentage: "5",
    })

    const url = `${this.baseUrl}/portal?${params}`
    // console.log("Portals Execute URL:", url) // Debug log to verify URL format

    const response = await fetch(url)

    if (!response.ok) {
      if (response.status === 503) {
        // {message: "Service Unavailable"}
        throw new Error("Portals Zap API is currently unavailable")
      }
      throw new Error(`Portals API error: ${response.status} ${response.statusText}`)
    }

    const json = await response.json()
    console.log("Portals API Execute Response", json)

    return json
  }

  /**
   * Check approval status and get approval transaction data if needed
   *
   * @param request - The approval request parameters
   * @returns Promise resolving to the approval response
   */
  async checkApproval(request: PortalsApprovalRequest): Promise<PortalsApprovalResponse> {
    const params = new URLSearchParams({
      sender: request.sender,
      inputToken: request.inputToken,
      inputAmount: request.inputAmount,
    })

    const url = `${this.baseUrl}/approval?${params}`
    // console.log("Portals Approval URL:", url) // Debug log to verify URL format

    const response = await fetch(url)

    if (!response.ok) {
      if (response.status === 503) {
        // {message: "Service Unavailable"}
        throw new Error("Portals Zap API is currently unavailable")
      }
      throw new Error(`Portals API error: ${response.status} ${response.statusText}`)
    }

    const json = await response.json()
    console.log("Portals API Check Approval Response:", json)

    return json
  }
}
